import { useCallback, useEffect, useRef, useState } from 'react'

import { useSelector } from 'react-redux'

import { makeSelectSegmentAnalyticsMetadata } from '@webapp/core/stores/global/selectors'
import { useProcurifyIntl } from '@webapp/hooks'
import { type Enum } from '@webapp/infrastructure/types/Enum'
import messages from './messages'
import { type ConversationStreamError } from '../../business-logic/interfaces/IConversationRepository'
import { type ConversationStartContext } from '../../business-logic/types/ConversationTurnPayload'
import { resumeConversation } from '../../business-logic/use-cases/ResumeConversationUseCase'
import { runConversationTurn } from '../../business-logic/use-cases/RunConversationTurnUseCase'
import { sendConversationTechnicalMessage } from '../../business-logic/use-cases/SendConversationTechnicalMessageUseCase'
import { GUIDED_INTAKE_EVENTS } from '../../constants'
import { getGuidedIntakePageType } from '../../utils/getGuidedIntakePageType'
import { type ConversationTechnicalMessage } from '../../value-objects/ConversationTechnicalMessage'
import { type ConversationTelemetryEvent } from '../../value-objects/ConversationTelemetryEvent'
import {
  createConversationTurnItem,
  type ConversationCatalogItemComponent,
  type ConversationComponent,
  type ConversationTurnItem,
  type ConversationTurnItemConstructor,
} from '../../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationItemKinds,
  GuidedIntakeEntryPoints,
  type GuidedIntakeEntryPointEnum,
} from '../../value-objects/enums'

export const ConversationStreamStatuses = {
  IDLE: 'idle',
  STREAMING: 'streaming',
  DONE: 'done',
  ERROR: 'error',
} as const

export type ConversationStreamStatusEnum = Enum<
  typeof ConversationStreamStatuses
>

export const ConversationBootstrapModes = {
  NEW: 'new',
  RESUME: 'resume',
} as const

export type ConversationBootstrapModeEnum = Enum<
  typeof ConversationBootstrapModes
>

export type ConversationBootstrap =
  | {
      mode: typeof ConversationBootstrapModes.NEW
      initialQuery: string
      startContext?: ConversationStartContext
    }
  | { mode: typeof ConversationBootstrapModes.RESUME; conversationId: string }

export type ConversationTechnicalMessageHandlers = {
  onComponents: (components: ConversationComponent[]) => void
  onDone: () => void
  onError: (error: ConversationStreamError) => void
}

export interface UseConversationStreamResult {
  items: ConversationTurnItem[]
  status: ConversationStreamStatusEnum
  thinkingLabel: Nullable<string>
  telemetry: ConversationTelemetryEvent[]
  conversationId: Nullable<string>
  sendMessage: (message: string) => boolean
  sendSilentMessage: (message: string) => boolean
  sendTechnicalMessage: (
    technicalMessage: ConversationTechnicalMessage,
    handlers: ConversationTechnicalMessageHandlers
  ) => Nullable<() => void>
}

export const useConversationStream = (
  bootstrap: ConversationBootstrap
): UseConversationStreamResult => {
  const intl = useProcurifyIntl()
  const segmentAnalyticsMetadata = useSelector(
    makeSelectSegmentAnalyticsMetadata()
  )
  // Formatted eagerly so hydrate depends on a stable string — useProcurifyIntl
  // returns a fresh object every render, and an intl-dependent hydrate would
  // re-run the bootstrap effect on each render, aborting and re-firing the
  // in-flight turn indefinitely.
  const resumeFailedMessage = intl.formatMessage(messages.resumeFailed)

  const [items, setItems] = useState<ConversationTurnItem[]>([])
  const [status, setStatus] = useState<ConversationStreamStatusEnum>(
    ConversationStreamStatuses.IDLE
  )
  const [telemetry, setTelemetry] = useState<ConversationTelemetryEvent[]>([])
  const [thinkingLabel, setThinkingLabel] = useState<Nullable<string>>(null)
  const [conversationId, setConversationId] = useState<Nullable<string>>(null)

  const nextIdRef = useRef(0)
  const disconnectRef = useRef<Nullable<() => void>>(null)
  const conversationIdRef = useRef<Nullable<string>>(null)
  const isStreamingRef = useRef(false)
  const turnIndexRef = useRef(0)
  const turnResultsRef = useRef(
    new Map<string, ConversationCatalogItemComponent[]>()
  )
  const technicalDisconnectsRef = useRef(new Set<() => void>())
  const isUnmountedRef = useRef(false)
  const hasSeededRef = useRef(false)
  const entryPointRef = useRef<GuidedIntakeEntryPointEnum>(
    GuidedIntakeEntryPoints.GLOBAL_SEARCH
  )

  const appendItem = useCallback(
    (constructor: ConversationTurnItemConstructor) => {
      setItems((prev) => [
        ...prev,
        createConversationTurnItem(constructor, nextIdRef.current++),
      ])
    },
    []
  )

  const upsertItem = useCallback(
    (constructor: ConversationTurnItemConstructor) => {
      if (constructor.kind !== ConversationItemKinds.ENVELOPE) {
        appendItem(constructor)
        return
      }
      setItems((prev) => {
        const index = prev.findIndex(
          (item) =>
            item.kind === ConversationItemKinds.ENVELOPE &&
            item.envelopeId === constructor.envelopeId
        )
        if (index === -1) {
          return [
            ...prev,
            createConversationTurnItem(constructor, nextIdRef.current++),
          ]
        }
        const next = prev.slice()
        next[index] = createConversationTurnItem(constructor, prev[index].id)
        return next
      })
    },
    [appendItem]
  )

  const markEnvelopesAnswered = useCallback(() => {
    setItems((prev) =>
      prev.map((item) =>
        item.kind === ConversationItemKinds.ENVELOPE && !item.isAnswered
          ? { ...item, isAnswered: true }
          : item
      )
    )
  }, [])

  const startSession = useCallback((sessionConversationId: string) => {
    conversationIdRef.current = sessionConversationId
    setConversationId(sessionConversationId)
  }, [])

  const domainId = segmentAnalyticsMetadata?.group?.id ?? null

  const startLiveSession = useCallback(
    (sessionConversationId: string) => {
      const isNewConversation = conversationIdRef.current === null
      startSession(sessionConversationId)
      if (!isNewConversation) return

      window.analytics?.track(GUIDED_INTAKE_EVENTS.CONVERSATION_STARTED, {
        conversation_id: sessionConversationId,
        domain_id: domainId,
        entry_point: entryPointRef.current,
        page_type: getGuidedIntakePageType(),
      })
    },
    [domainId, startSession]
  )

  const collectTurnResults = useCallback(
    (constructor: ConversationTurnItemConstructor) => {
      if (constructor.kind !== ConversationItemKinds.ENVELOPE) return
      const results = constructor.components.filter(
        (component): component is ConversationCatalogItemComponent =>
          component.type === ConversationComponentTypes.CATALOG_ITEM
      )
      if (results.length > 0) {
        turnResultsRef.current.set(constructor.envelopeId, results)
      }
    },
    []
  )

  const trackTurnResults = useCallback(() => {
    const results = ([] as ConversationCatalogItemComponent[]).concat(
      ...turnResultsRef.current.values()
    )
    turnResultsRef.current.clear()
    if (results.length === 0) return

    window.analytics?.track(GUIDED_INTAKE_EVENTS.RESULTS_RENDERED, {
      conversation_id: conversationIdRef.current,
      turn_index: turnIndexRef.current,
      result_count: results.length,
      positions: results.map((result) => result.rank ?? null),
    })
  }, [])

  const openTurn = useCallback(
    (message: string, startContext?: ConversationStartContext) => {
      setStatus(ConversationStreamStatuses.STREAMING)
      setThinkingLabel(null)
      isStreamingRef.current = true
      disconnectRef.current?.()
      disconnectRef.current = runConversationTurn(
        { message, conversationId: conversationIdRef.current, startContext },
        {
          onItem: (constructor) => {
            collectTurnResults(constructor)
            upsertItem(constructor)
          },
          onSessionStart: startLiveSession,
          onProgress: setThinkingLabel,
          onTelemetry: (event) => setTelemetry((prev) => [...prev, event]),
          onDone: () => {
            trackTurnResults()
            isStreamingRef.current = false
            setThinkingLabel(null)
            setStatus((prev) =>
              prev === ConversationStreamStatuses.ERROR
                ? prev
                : ConversationStreamStatuses.DONE
            )
          },
          onError: (error) => {
            isStreamingRef.current = false
            setThinkingLabel(null)
            appendItem({
              kind: ConversationItemKinds.ERROR,
              message: error.message ?? error.code,
            })
            setStatus(ConversationStreamStatuses.ERROR)
          },
        }
      )
    },
    [
      appendItem,
      collectTurnResults,
      startLiveSession,
      trackTurnResults,
      upsertItem,
    ]
  )

  const sendMessage = useCallback(
    (rawMessage: string) => {
      if (isStreamingRef.current) return false

      const message = rawMessage.trim()
      if (message === '') return false

      markEnvelopesAnswered()
      appendItem({
        kind: ConversationItemKinds.MESSAGE,
        sender: 'user',
        content: message,
      })

      // The opening message is sent before the stream names the conversation,
      // so it is reported by the conversation-started event instead.
      turnIndexRef.current += 1
      if (conversationIdRef.current !== null) {
        window.analytics?.track(GUIDED_INTAKE_EVENTS.USER_TURN, {
          conversation_id: conversationIdRef.current,
          turn_index: turnIndexRef.current,
          ts: new Date().toISOString(),
        })
      }

      openTurn(message)
      return true
    },
    [appendItem, markEnvelopesAnswered, openTurn]
  )

  // Cart mutations post a turn to the agent without echoing a user bubble in
  // the transcript; the agent streams back an updated cart envelope.
  const sendSilentMessage = useCallback(
    (rawMessage: string) => {
      if (isStreamingRef.current) return false

      const message = rawMessage.trim()
      if (message === '') return false

      openTurn(message)
      return true
    },
    [openTurn]
  )

  const sendTechnicalMessage = useCallback(
    (
      technicalMessage: ConversationTechnicalMessage,
      handlers: ConversationTechnicalMessageHandlers
    ): Nullable<() => void> => {
      const activeConversationId = conversationIdRef.current
      if (activeConversationId === null) return null

      let disconnect: Nullable<() => void> = null
      const whileMounted =
        <TArgs extends unknown[]>(callback: (...args: TArgs) => void) =>
        (...args: TArgs) => {
          if (!isUnmountedRef.current) callback(...args)
        }

      disconnect = sendConversationTechnicalMessage(
        { technicalMessage, conversationId: activeConversationId },
        {
          onItem: whileMounted((constructor) => {
            if (constructor.kind !== ConversationItemKinds.ENVELOPE) return
            handlers.onComponents(constructor.components)
          }),
          onSessionStart: () => undefined,
          onProgress: () => undefined,
          onTelemetry: whileMounted((event) =>
            setTelemetry((prev) => [...prev, event])
          ),
          onDone: () => {
            if (disconnect) technicalDisconnectsRef.current.delete(disconnect)
            whileMounted(handlers.onDone)()
          },
          onError: whileMounted(handlers.onError),
        }
      )
      technicalDisconnectsRef.current.add(disconnect)
      return disconnect
    },
    []
  )

  // Hydration replaces the transcript wholesale; the user sends the next
  // message themselves — no turn is auto-fired on resume.
  const hydrate = useCallback(
    (resumedConversationId: string) => {
      let isActive = true
      setStatus(ConversationStreamStatuses.STREAMING)
      isStreamingRef.current = true

      resumeConversation(resumedConversationId)
        .then((constructors) => {
          if (!isActive) return
          setItems(
            constructors.map((constructor) =>
              createConversationTurnItem(constructor, nextIdRef.current++)
            )
          )
          turnIndexRef.current = constructors.filter(
            (constructor) =>
              constructor.kind === ConversationItemKinds.MESSAGE &&
              constructor.sender === 'user'
          ).length
          startSession(resumedConversationId)
          isStreamingRef.current = false
          setStatus(ConversationStreamStatuses.DONE)
        })
        .catch(() => {
          if (!isActive) return
          // Even when the transcript fails to load, the user chose to continue
          // this conversation — bind its id so the next message resumes it
          // server-side instead of silently starting a new one.
          startSession(resumedConversationId)
          appendItem({
            kind: ConversationItemKinds.ERROR,
            message: resumeFailedMessage,
          })
          isStreamingRef.current = false
          setStatus(ConversationStreamStatuses.ERROR)
        })

      return () => {
        isActive = false
      }
    },
    [appendItem, resumeFailedMessage, startSession]
  )

  useEffect(() => {
    if (bootstrap.mode === ConversationBootstrapModes.RESUME) {
      const cancelHydration = hydrate(bootstrap.conversationId)
      return () => {
        cancelHydration()
        disconnectRef.current?.()
      }
    }

    if (!hasSeededRef.current) {
      hasSeededRef.current = true
      entryPointRef.current =
        bootstrap.startContext?.entryPoint ??
        GuidedIntakeEntryPoints.GLOBAL_SEARCH
      if (bootstrap.initialQuery !== '') {
        turnIndexRef.current = 1
        appendItem({
          kind: ConversationItemKinds.MESSAGE,
          sender: 'user',
          content: bootstrap.initialQuery,
        })
      }
      openTurn(bootstrap.initialQuery, bootstrap.startContext)
    }
    return () => disconnectRef.current?.()
  }, [appendItem, bootstrap, hydrate, openTurn])

  useEffect(() => {
    const technicalDisconnects = technicalDisconnectsRef.current
    return () => {
      isUnmountedRef.current = true
      technicalDisconnects.forEach((disconnect) => disconnect())
      technicalDisconnects.clear()
    }
  }, [])

  return {
    items,
    status,
    thinkingLabel,
    telemetry,
    conversationId,
    sendMessage,
    sendSilentMessage,
    sendTechnicalMessage,
  }
}
